import { create, type StoreApi, type UseBoundStore } from 'zustand';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: string;
  tenantId?: string;
}

export interface AuthState<TUser extends AuthUser = AuthUser> {
  token: string | null;
  user: TUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setAuth: (token: string, user: TUser) => void;
  clearAuth: () => void;
  setLoading: (loading: boolean) => void;
}

export interface RegisterTenantPayload {
  schoolName: string;
  subdomain: string;
  adminName: string;
  adminEmail: string;
  adminPassword: string;
}

export interface CreateAuthStoreOptions {
  /** localStorage key used to persist the bearer token (e.g. `edupra_token`). */
  tokenKey: string;
  /** Base URL for auth endpoints (e.g. `/api/auth`). */
  apiBase: string;
  /** Where to redirect the browser on logout. Defaults to `/login`. */
  logoutRedirect?: string;
}

export interface AuthStoreApi<TUser extends AuthUser = AuthUser> {
  useAuthStore: UseBoundStore<StoreApi<AuthState<TUser>>>;
  useAuth: () => {
    token: string | null;
    user: TUser | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    logout: () => void;
  };
  getToken: () => string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  registerTenant: (payload: RegisterTenantPayload) => Promise<void>;
}

/**
 * Build a tenant-scoped or platform-scoped auth store. Each realm should call
 * this factory once with a distinct `tokenKey` + `apiBase` so the
 * localStorage entries and HTTP endpoints don't collide.
 */
export function createAuthStore<TUser extends AuthUser = AuthUser>(
  options: CreateAuthStoreOptions,
): AuthStoreApi<TUser> {
  const { tokenKey, apiBase, logoutRedirect = '/login' } = options;

  const useAuthStore = create<AuthState<TUser>>((set) => ({
    token: typeof window === 'undefined' ? null : localStorage.getItem(tokenKey),
    user: null,
    isAuthenticated: false,
    isLoading: true,
    setAuth: (token: string, user: TUser) => {
      if (typeof window !== 'undefined') {
        localStorage.setItem(tokenKey, token);
      }
      set({ token, user, isAuthenticated: true, isLoading: false });
    },
    clearAuth: () => {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(tokenKey);
      }
      set({ token: null, user: null, isAuthenticated: false, isLoading: false });
    },
    setLoading: (loading: boolean) => set({ isLoading: loading }),
  }));

  function getToken(): string | null {
    return useAuthStore.getState().token;
  }

  function logout(): void {
    useAuthStore.getState().clearAuth();
    if (typeof window !== 'undefined') {
      window.location.href = logoutRedirect;
    }
  }

  function useAuth() {
    const store = useAuthStore();
    return {
      token: store.token,
      user: store.user,
      isAuthenticated: store.isAuthenticated,
      isLoading: store.isLoading,
      logout,
    };
  }

  async function login(email: string, password: string): Promise<void> {
    const res = await fetch(`${apiBase}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const body = await res.json();
    if (!res.ok || !body.success) {
      throw new Error(body.error ?? body.message ?? 'Login failed');
    }

    useAuthStore.getState().setAuth(body.data.token, body.data.user as TUser);
  }

  async function registerTenant(payload: RegisterTenantPayload): Promise<void> {
    const res = await fetch(`${apiBase}/register-tenant`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const body = await res.json();
    if (!res.ok || !body.success) {
      throw new Error(body.error ?? body.message ?? 'Registration failed');
    }

    useAuthStore.getState().setAuth(body.data.token, body.data.user as TUser);
  }

  async function checkAuth(): Promise<void> {
    if (typeof window === 'undefined') {
      useAuthStore.getState().clearAuth();
      return;
    }
    const token = localStorage.getItem(tokenKey);
    if (!token) {
      useAuthStore.getState().clearAuth();
      return;
    }

    try {
      const res = await fetch(`${apiBase}/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const body = await res.json();
      if (!res.ok || !body.success) {
        useAuthStore.getState().clearAuth();
        return;
      }

      useAuthStore.getState().setAuth(token, body.data.user as TUser);
    } catch {
      useAuthStore.getState().clearAuth();
    }
  }

  return {
    useAuthStore,
    useAuth,
    getToken,
    login,
    logout,
    checkAuth,
    registerTenant,
  };
}
